// Notes on any record, and tasks made from them (ERP-72, ERP-73).
//
// `<Notes entityType="Invoice" entityId={id} label={number} />` on a record page. A note written
// there also shows on the records it involves — the invoice's customer, the contract's borrower —
// with where it came from; the server works that out. Replies answer a note; "Task" turns one into
// work for somebody, which then waits on their dashboard.
import {
  date,
  del,
  post,
  put,
  recordRoutes,
  relTime,
  todayIso,
  useAction,
  useApi,
  useAuth,
} from "@keel/core";
import { useEffect, useState, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { ENGAGEMENTS, LogEngagement } from "./Activity";
import { Select } from "./Select";
import { Field, Icon, Modal } from "./ui";

export interface TaskBrief {
  id: string;
  title: string;
  assigneeName: string | null;
  dueOn: string | null;
  status: "open" | "done";
}

export interface Note {
  id: string;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  /** What it was written on: "Invoice", "Loan agreement". */
  category: string;
  /** Written on this record, not shown here because this record is involved. */
  here: boolean;
  parentId: string | null;
  body: string;
  isPinned: boolean;
  authorName: string | null;
  mine: boolean;
  canEdit: boolean;
  editedAt: string | null;
  createdAt: string;
  /** note | call | meeting | email | visit (ERP-74). */
  kind: string;
  occurredAt: string;
  replies: Note[];
  tasks: TaskBrief[];
}

export interface Task {
  id: string;
  companyId: string | null;
  title: string;
  details: string | null;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  category: string | null;
  noteId: string | null;
  assigneeId: string;
  assigneeName: string | null;
  dueOn: string | null;
  priority: "low" | "normal" | "high";
  status: "open" | "done";
  createdBy: string | null;
  createdByName: string | null;
  doneAt: string | null;
  doneByName: string | null;
  createdAt: string;
  canEdit: boolean;
}

// ── the record this page is about, for the top bar's Notes button ──────────────────────────────
export interface RecordRef {
  entityType: string;
  entityId: string;
  label?: string | null;
}
let current: RecordRef | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

/**
 * A record page says which record it shows, so the top bar can offer its notes in the same place
 * on every page. Pass `null` while the record is loading.
 */
export function useRecordNotes(r: RecordRef | null) {
  const key = r ? `${r.entityType}:${r.entityId}:${r.label ?? ""}` : "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the record on purpose
  useEffect(() => {
    if (!r) return;
    const mine = { ...r };
    current = mine;
    emit();
    return () => {
      if (current === mine) {
        current = null;
        emit();
      }
    };
  }, [key]);
}

/** The record the page on screen is about, if it said. */
export const useCurrentRecord = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );

/** The link to a note's or task's record, when a module has a page for it. */
export function useRecordHref() {
  const { me } = useAuth();
  const routes = recordRoutes(me.modules);
  return (type: string | null, id: string | null) => (type && id && routes[type] ? routes[type] + id : null);
}

export function Notes({
  entityType,
  entityId,
  label,
  title = "Internal notes",
}: {
  entityType: string;
  entityId: string;
  /** How the record reads where its notes travel to: its number or name. */
  label?: string | null;
  title?: string;
}) {
  const { run, busy } = useAction();
  const path = `/notes?entityType=${entityType}&entityId=${entityId}`;
  const q = useApi<Note[]>(path);
  const [text, setText] = useState("");
  const [task, setTask] = useState<{ note?: Note } | null>(null);
  const [log, setLog] = useState(false);
  if (q.error) return null; // no right to read this record's notes
  const notes = q.data ?? [];
  const add = async () => {
    if (!text.trim()) return;
    const ok = await run(() =>
      post("/notes", { entityType, entityId, entityLabel: label ?? null, body: text }),
    );
    if (ok) setText("");
  };
  return (
    <section className="notes">
      <header className="notes-head">
        {title ? (
          <h3 className="card-title">
            {title}
            {notes.length > 0 && <span className="count">{notes.length}</span>}
          </h3>
        ) : (
          <span />
        )}
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="btn btn-sm" onClick={() => setLog(true)}>
            <Icon name="send" size={13} />
            Log call…
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setTask({})}>
            <Icon name="check" size={13} />
            New task
          </button>
        </div>
      </header>
      <div className="note-compose">
        <textarea
          rows={2}
          value={text}
          placeholder="Write a note… (Ctrl+Enter to add)"
          aria-label="New note"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={busy || !text.trim()}
          onClick={add}
        >
          Add note
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="sm muted">
          No notes yet. Internal: never printed on a document. A note here also shows on the customer or
          supplier involved.
        </p>
      ) : (
        <ol className="note-list">
          {notes.map((n) => (
            <NoteItem key={n.id} note={n} onTask={(note) => setTask({ note })} />
          ))}
        </ol>
      )}
      {log && (
        <LogEngagement
          entityType={entityType}
          entityId={entityId}
          label={label}
          onClose={() => setLog(false)}
        />
      )}
      {task && (
        <TaskDialog
          note={task.note}
          record={task.note ? undefined : { entityType, entityId, label: label ?? null }}
          onClose={() => setTask(null)}
        />
      )}
    </section>
  );
}

function NoteItem({ note: n, onTask, reply }: { note: Note; onTask: (n: Note) => void; reply?: boolean }) {
  const { run, busy } = useAction();
  const href = useRecordHref();
  const [editing, setEditing] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const to = href(n.entityType, n.entityId);
  return (
    <li className={`note ${n.isPinned ? "pinned" : ""} ${reply ? "reply" : ""}`}>
      <div className="note-meta">
        <b>{n.authorName ?? "System"}</b>
        {n.kind !== "note" && (
          <span className="note-kind">{ENGAGEMENTS.find((k) => k.value === n.kind)?.label ?? n.kind}</span>
        )}
        <time className="muted" title={new Date(n.occurredAt).toLocaleString()}>
          {relTime(n.occurredAt)}
        </time>
        {n.editedAt && <span className="muted">· edited</span>}
        {n.isPinned && !reply && (
          <span className="perm-tag">
            <Icon name="pin" size={11} /> Pinned
          </span>
        )}
        {!n.here && !reply && (
          <span className="note-from">
            {n.category} {to ? <Link to={to}>{n.entityLabel ?? "open"}</Link> : (n.entityLabel ?? "")}
          </span>
        )}
      </div>
      {editing !== null ? (
        <div className="note-compose">
          <textarea
            rows={2}
            value={editing}
            aria-label="Edit note"
            onChange={(e) => setEditing(e.target.value)}
          />
          <div className="row">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy || !editing.trim()}
              onClick={async () => {
                if (await run(() => put(`/notes/${n.id}`, { body: editing }))) setEditing(null);
              }}
            >
              Save
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="note-body">{n.body}</p>
      )}
      {n.tasks.length > 0 && (
        <ul className="note-tasks">
          {n.tasks.map((t) => (
            <li key={t.id} className={t.status === "done" ? "done" : ""}>
              <Icon name={t.status === "done" ? "check" : "timer"} size={12} />
              <Link to="/tasks">{t.title}</Link>
              <span className="muted">
                {" "}
                · {t.assigneeName}
                {t.dueOn ? ` · due ${date(t.dueOn)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="note-actions">
        {!reply && (
          <button type="button" className="link-btn" onClick={() => setAnswer("")}>
            Reply
          </button>
        )}
        <button type="button" className="link-btn" onClick={() => onTask(n)}>
          Task
        </button>
        {!reply && (
          <button
            type="button"
            className="link-btn"
            disabled={busy}
            onClick={() => run(() => put(`/notes/${n.id}`, { isPinned: !n.isPinned }))}
          >
            {n.isPinned ? "Unpin" : "Pin"}
          </button>
        )}
        {n.canEdit && (
          <>
            <button type="button" className="link-btn" onClick={() => setEditing(n.body)}>
              Edit
            </button>
            <button
              type="button"
              className="link-btn danger"
              disabled={busy}
              onClick={() =>
                confirm(n.replies.length ? "Delete this note and its replies?" : "Delete this note?") &&
                run(() => del(`/notes/${n.id}`), "Note deleted")
              }
            >
              Delete
            </button>
          </>
        )}
      </div>
      {answer !== null && (
        <div className="note-compose">
          <textarea
            rows={2}
            value={answer}
            aria-label="Reply"
            placeholder="Reply…"
            onChange={(e) => setAnswer(e.target.value)}
          />
          <div className="row">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy || !answer.trim()}
              onClick={async () => {
                const ok = await run(() =>
                  post("/notes", {
                    entityType: n.entityType,
                    entityId: n.entityId,
                    parentId: n.id,
                    body: answer,
                  }),
                );
                if (ok) setAnswer(null);
              }}
            >
              Reply
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setAnswer(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {n.replies.length > 0 && (
        <ol className="note-list replies">
          {n.replies.map((r) => (
            <NoteItem key={r.id} note={r} onTask={onTask} reply />
          ))}
        </ol>
      )}
    </li>
  );
}

/** The round "done" button a task row starts with. */
export function TaskCheck({ task }: { task: Task }) {
  const { run, busy } = useAction();
  const done = task.status === "done";
  return (
    <button
      type="button"
      className={`task-check ${done ? "on" : ""}`}
      aria-label={done ? `Reopen ${task.title}` : `Mark ${task.title} done`}
      title={done ? "Reopen" : "Mark done"}
      disabled={busy || !task.canEdit}
      onClick={(e) => {
        e.stopPropagation();
        run(() => put(`/tasks/${task.id}`, { status: done ? "open" : "done" }), done ? "Reopened" : "Done");
      }}
    >
      <Icon name="check" size={12} />
    </button>
  );
}

/** Give somebody a task: from a note, about a record, or on its own. */
export function TaskDialog({
  note,
  record,
  task,
  onClose,
}: {
  note?: Note;
  record?: { entityType: string; entityId: string; label: string | null };
  /** Edit this one instead of making a new one. */
  task?: Task;
  onClose: () => void;
}) {
  const { me } = useAuth();
  const { run, busy } = useAction();
  const people = useApi<{ id: string; name: string; email: string }[]>("/tasks/assignees").data ?? [];
  const firstLine = note?.body.split("\n")[0]?.slice(0, 120) ?? "";
  const [f, setF] = useState({
    title: task?.title ?? firstLine,
    details: task?.details ?? (note && note.body !== firstLine ? note.body : ""),
    assigneeId: task?.assigneeId ?? me.user?.id ?? "",
    dueOn: task?.dueOn ?? "",
    priority: task?.priority ?? "normal",
  });
  const about = note
    ? `${note.category} ${note.entityLabel ?? ""}`.trim()
    : task
      ? task.category && `${task.category} ${task.entityLabel ?? ""}`.trim()
      : record?.label;
  return (
    <Modal
      title={task ? "Edit task" : "New task"}
      width={520}
      onClose={onClose}
      busy={busy}
      submit={task ? "Save" : "Create task"}
      onSubmit={async () => {
        const body = {
          title: f.title,
          details: f.details || null,
          assigneeId: f.assigneeId,
          dueOn: f.dueOn || null,
          priority: f.priority,
        };
        const ok = await run(
          () =>
            task
              ? put(`/tasks/${task.id}`, body)
              : post("/tasks", {
                  ...body,
                  noteId: note?.id ?? null,
                  entityType: note ? null : (record?.entityType ?? null),
                  entityId: note ? null : (record?.entityId ?? null),
                  entityLabel: note ? null : (record?.label ?? null),
                }),
          task ? "Task saved" : "Task created",
        );
        if (ok) onClose();
      }}
    >
      <div className="form-grid">
        {about && (
          <p className="sm muted span-2" style={{ margin: 0 }}>
            About {about}
          </p>
        )}
        <Field label="Task" span>
          <input
            value={f.title}
            maxLength={200}
            placeholder="Call the customer about the payment"
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label="Details" span>
          <textarea rows={3} value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} />
        </Field>
        <Field label="Assign to" tip="Whoever it is given to sees it on their dashboard until it is done.">
          <Select value={f.assigneeId} onChange={(e) => setF({ ...f, assigneeId: e.target.value })}>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id === me.user?.id ? `${p.name} (me)` : p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Due">
          <input
            type="date"
            min={task ? undefined : todayIso()}
            value={f.dueOn}
            onChange={(e) => setF({ ...f, dueOn: e.target.value })}
          />
        </Field>
        <Field label="Priority">
          <Select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value as Task["priority"] })}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </Select>
        </Field>
      </div>
    </Modal>
  );
}
